import { useCallback, useEffect, useRef, useState } from 'react';

import { NiceVerifiedData, getNiceResult, postNiceAuthUrl } from '@/api/nice';

const POPUP_FEATURES = 'width=480,height=640,scrollbars=yes,resizable=yes';
const POLL_INTERVAL_MS = 1500;
const CLOSE_GRACE_MS = 4000;
const TIMEOUT_MS = 5 * 60 * 1000;

type UseNiceMobileAuthOptions = {
  memberId?: number;
  onSuccess?: (data: NiceVerifiedData) => void;
  onError?: (message: string) => void;
  /** 로그인 번호와 실명인증 번호가 다를 때 호출 (인증 미완료로 처리) */
  onPhoneMismatch?: (data: NiceVerifiedData) => void;
};

const getBackendOrigin = (): string | null => {
  try {
    return new URL(process.env.NEW_REACT_APP_SERVER_URL as string).origin;
  } catch {
    return null;
  }
};

/**
 * NICE 휴대폰 본인확인(PASS) 플로우 훅.
 * 인증 URL 을 발급받아 팝업으로 표준창을 띄우고, postMessage(가속) + 결과 폴링으로 완료를 감지한다.
 */
const useNiceMobileAuth = ({ memberId, onSuccess, onError, onPhoneMismatch }: UseNiceMobileAuthOptions = {}) => {
  const [isVerified, setIsVerified] = useState(false);
  const [verifiedData, setVerifiedData] = useState<NiceVerifiedData | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const runningRef = useRef(false);
  const cleanupRef = useRef<() => void>(() => {});
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const onPhoneMismatchRef = useRef(onPhoneMismatch);
  const memberIdRef = useRef(memberId);
  onSuccessRef.current = onSuccess;
  onErrorRef.current = onError;
  onPhoneMismatchRef.current = onPhoneMismatch;
  memberIdRef.current = memberId;

  useEffect(() => () => cleanupRef.current(), []);

  const startVerification = useCallback(async () => {
    if (runningRef.current || isVerified) return;
    runningRef.current = true;
    setIsLoading(true);

    const popup = window.open('', 'niceMobileAuth', POPUP_FEATURES);
    if (!popup) {
      runningRef.current = false;
      setIsLoading(false);
      onErrorRef.current?.('팝업이 차단되었습니다. 팝업 허용 후 다시 시도해주세요.');
      return;
    }

    let done = false;
    let key = '';
    let startAt = Date.now();
    let closedAt = 0;
    let fetching = false;
    let pollTimer = 0;
    let messageHandler: (event: MessageEvent) => void = () => {};

    const cleanup = () => {
      if (pollTimer) {
        window.clearInterval(pollTimer);
        pollTimer = 0;
      }
      window.removeEventListener('message', messageHandler);
      // opener 측에서 팝업을 닫는다. (NICE 페이지의 COOP 로 팝업 자체 window.close() 가 막힐 수 있음)
      try {
        if (popup && !popup.closed) {
          popup.close();
        }
      } catch {
        /* COOP: ignore */
      }
      runningRef.current = false;
    };
    cleanupRef.current = cleanup;

    const finishSuccess = (data: NiceVerifiedData) => {
      if (done) return;
      done = true;
      cleanup();
      setVerifiedData(data);
      setIsVerified(true);
      setIsLoading(false);
      onSuccessRef.current?.(data);
    };

    const finishError = (message: string) => {
      if (done) return;
      done = true;
      cleanup();
      setIsLoading(false);
      onErrorRef.current?.(message);
    };

    const finishMismatch = (data: NiceVerifiedData) => {
      if (done) return;
      done = true;
      cleanup();
      setIsLoading(false);
      // 다른 번호로 인증됨 → 인증 완료로 처리하지 않고 경고 콜백 호출
      onPhoneMismatchRef.current?.(data);
    };

    const pollOnce = async () => {
      if (done || fetching || !key) return;
      fetching = true;
      try {
        const result = await getNiceResult(key);
        if (result?.success && result.data) {
          if (result.phoneMatched === false) {
            finishMismatch(result.data);
          } else {
            finishSuccess(result.data);
          }
          return;
        }
      } catch (e) {
        // 일시적 오류는 무시하고 다음 폴링에서 재시도
        console.error('[NICE Auth Poll Error]', e);
      } finally {
        fetching = false;
      }

      if (done) return;
      const now = Date.now();
      if (popup.closed) {
        if (!closedAt) {
          closedAt = now;
        } else if (now - closedAt > CLOSE_GRACE_MS) {
          finishError('본인인증이 완료되지 않았습니다.');
        }
      }
      if (now - startAt > TIMEOUT_MS) {
        finishError('본인인증 시간이 초과되었습니다. 다시 시도해주세요.');
      }
    };

    messageHandler = (event: MessageEvent) => {
      const backendOrigin = getBackendOrigin();
      if (backendOrigin && event.origin !== backendOrigin) return;
      const data = event.data as { type?: string; success?: boolean; key?: string } | null;
      if (!data || data.type !== 'NICE_AUTH') return;
      if (key && data.key && data.key !== key) return;
      // 성공/실패 모두 즉시 결과 조회를 트리거(가속). 실제 판정은 결과 조회로 처리.
      pollOnce();
    };
    window.addEventListener('message', messageHandler);

    try {
      const res = await postNiceAuthUrl(memberIdRef.current);
      key = res.key;
      startAt = Date.now();
      popup.location.replace(res.authUrl);
    } catch (e: any) {
      console.error('[NICE Auth URL Error]', e);
      try {
        popup.close();
      } catch {
        /* COOP: ignore */
      }
      finishError(e?.message || '본인인증 요청 중 오류가 발생했습니다.');
      return;
    }

    pollTimer = window.setInterval(pollOnce, POLL_INTERVAL_MS);
  }, [isVerified]);

  return { isVerified, verifiedData, isLoading, startVerification };
};

export default useNiceMobileAuth;
